import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, eur, deDate } from "../../lib/api";
import {
  ShoppingBag, ArrowRightLeft, Package, Sparkles, FileText, Users, Plus, TrendingUp, ScanText,
} from "lucide-react";

const ACTIONS = [
  { to: "/app/ankauf/neu", label: "Neuer Ankauf", icon: ShoppingBag },
  { to: "/app/verkauf/neu", label: "Neuer Verkauf", icon: ArrowRightLeft },
  { to: "/app/ki-import", label: "KI Import", icon: Sparkles },
  { to: "/app/dokumente?scan=1", label: "Scannen", icon: ScanText },
  { to: "/app/bestand?neu=1", label: "Neuer Artikel", icon: Package },
  { to: "/app/kunden?neu=1", label: "Neuer Kunde", icon: Users },
  { to: "/app/verkaeufer?neu=1", label: "Neuer Verkäufer", icon: Users },
  { to: "/app/ankauf", label: "Kaufvertrag", icon: FileText },
];

function Kpi({ label, value, sub }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">{label}</div>
      <div className="font-display text-2xl font-semibold tracking-tight mt-1.5" data-testid={`kpi-${label.toLowerCase().replace(/[^a-zäöü]/g, "-")}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data)).catch((e) => setError(e?.response?.data?.detail || ""));
    api.get("/stats/insights").then((r) => setInsights(r.data)).catch(() => setInsights([]));
  }, []);

  if (!stats) return <div className="text-sm font-mono text-zinc-400" data-testid="dashboard-loading">{error || "Dashboard wird geladen…"}</div>;

  const { today, month, stock } = stats;
  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">{new Date().getHours() < 11 ? "Guten Morgen" : new Date().getHours() < 18 ? "Guten Tag" : "Guten Abend"} 👋 · {deDate(new Date().toISOString())}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Dein L19 Überblick</h1>
        </div>
      </div>

      <div className="bg-zinc-900 text-white rounded-2xl p-5 sm:p-6" data-testid="l19-ki-assistant">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">
          <Sparkles className="w-4 h-4" /> L19 KI – Dein Assistent
        </div>
        {insights === null && <div className="text-sm text-zinc-500 font-mono">Analysiere…</div>}
        <div className="space-y-2">
          {(insights || []).map((i, x) => (
            <div key={x} className="flex items-start gap-2 text-sm text-zinc-200" data-testid={`ki-insight-${x}`}>
              <span className="text-zinc-500 mt-0.5">→</span> {i.text}
            </div>
          ))}
        </div>
        <Link to="/app/ki-import" data-testid="ki-assistant-cta" className="inline-flex items-center gap-2 mt-4 text-xs font-mono bg-white text-zinc-900 px-3 py-2 rounded-lg hover:bg-zinc-200 transition-colors">
          <Sparkles className="w-3.5 h-3.5" /> KI-Import öffnen
        </Link>
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">Kennzahlen</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="overview-kpis">
          <Kpi label="Umsatz (Monat)" value={eur(month.revenue)} />
          <Kpi label="Gewinn (Monat)" value={eur(month.profit)} />
          <Kpi label="Offene Ankäufe" value={stats.open_contracts} />
          <Kpi label="Offene Verkäufe" value={stats.reserved} />
          <Kpi label="Lagerbestand" value={stock.count} />
          <Kpi label="Neue Kunden" value={stats.new_customers_month} />
          <Kpi label="Offene Dokumente" value={stats.open_contracts} />
          <Kpi label="Wichtige Aufgaben" value={(insights || []).filter((i) => i.key !== "ok").length} />
        </div>
      </div>

      <div data-testid="quick-actions-section">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">Schnellaktionen</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="quick-actions">
          {ACTIONS.map((a) => (
            <Link key={a.label} to={a.to} data-testid={`quick-${a.label.toLowerCase().replace(/[^a-zäöü]/g, "-")}`}
              className="bg-white border border-zinc-200 rounded-xl p-4 hover:border-zinc-900 active:bg-zinc-50 transition-colors group min-h-[72px]">
              <a.icon className="w-5 h-5 text-zinc-400 group-hover:text-zinc-900 transition-colors" />
              <div className="text-sm font-medium mt-2.5">{a.label}</div>
            </Link>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">Heute</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Kpi label="Ankäufe" value={today.purchases} />
          <Kpi label="Verkäufe" value={today.sales} />
          <Kpi label="Umsatz" value={eur(today.revenue)} />
          <Kpi label="Gewinn" value={eur(today.profit)} />
          <Kpi label="Neue Kunden" value={today.new_customers} />
          <Kpi label="Offene Vorgänge" value={stats.open_contracts} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
          <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">Bestand</div>
          <div className="grid grid-cols-2 gap-3">
            <Kpi label="Artikel im Lager" value={stock.count} />
            <Kpi label="Bestandswert" value={eur(stock.value)} />
            <Kpi label="Verkaufte Artikel" value={stock.sold_count} />
            <Kpi label="Ø Lagerdauer" value={`${stock.avg_duration} Tage`} />
          </div>
          <div className="mt-3 bg-zinc-900 text-white rounded-xl p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Möglicher Gewinn im Bestand</div>
              <div className="font-display text-xl font-semibold mt-1">{eur(stock.potential_profit)}</div>
            </div>
            <TrendingUp className="w-6 h-6 text-zinc-500" />
          </div>
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">Finanzen · laufender Monat</div>
          <div className="grid grid-cols-2 gap-3">
            <Kpi label="Einkauf" value={eur(month.purchase_value)} sub={`${month.purchases} Ankäufe`} />
            <Kpi label="Verkauf" value={eur(month.revenue)} sub={`${month.sales} Verkäufe`} />
            <Kpi label="Gewinn" value={eur(month.profit)} sub={month.revenue ? `Marge ${((month.profit / month.revenue) * 100).toFixed(1)} %` : "–"} />
            <Kpi label="Steuer" value={eur(month.tax)} />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100 flex justify-between items-center">
            <h3 className="font-display font-medium">Letzte Ankäufe</h3>
            <Link to="/app/ankauf" className="text-xs font-mono text-zinc-400 hover:text-zinc-900">Alle →</Link>
          </div>
          <div className="divide-y divide-zinc-50">
            {stats.recent_purchases.length === 0 && <div className="p-5 text-sm text-zinc-400">Noch keine Ankäufe.</div>}
            {stats.recent_purchases.map((p) => (
              <div key={p.id} className="px-5 py-3 flex justify-between items-center text-sm">
                <span className="font-mono text-xs text-zinc-500">{p.number}</span>
                <span className="text-zinc-700">{p.seller_name}</span>
                <span className="font-medium">{eur(p.total)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100 flex justify-between items-center">
            <h3 className="font-display font-medium">Letzte Verkäufe</h3>
            <Link to="/app/verkauf" className="text-xs font-mono text-zinc-400 hover:text-zinc-900">Alle →</Link>
          </div>
          <div className="divide-y divide-zinc-50">
            {stats.recent_sales.length === 0 && <div className="p-5 text-sm text-zinc-400">Noch keine Verkäufe.</div>}
            {stats.recent_sales.map((s) => (
              <div key={s.id} className="px-5 py-3 flex justify-between items-center text-sm">
                <span className="font-mono text-xs text-zinc-500">{s.number}</span>
                <span className="text-zinc-700">{s.customer_name}</span>
                <span className="font-medium">{eur(s.total)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
